import { Checkbox, cn } from '@viapeople/components'
import type React from 'react'
import { useState } from 'react'
import InfoIcon from '../../icons/InfoIcon'
import { MarkdownPreview } from '../MarkdownEditor'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip'
import type { CheckboxCustomProps, ComponentProps } from './types'
import { getLabelFontSize } from './utils'

type FormsCheckboxProps = ComponentProps & {
	checked?: boolean
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	readOnly?: boolean
	hasError?: boolean
}

export function FormsCheckbox({ component, checked, onChange, readOnly, hasError, required }: FormsCheckboxProps) {
	const {
		label,
		labelColor,
		labelSize,
		labelWeight,
		checkboxColor,
		defaultChecked = false,
		tooltipEnabled,
		tooltipText,
	} = component.custom as CheckboxCustomProps
	const [internalChecked, setInternalChecked] = useState(defaultChecked)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInternalChecked(e.target.checked)
		onChange?.(e)
	}

	const showTooltip = tooltipEnabled && tooltipText?.trim()

	return (
		<div className='flex flex-col gap-1'>
			<div
				key={component.id}
				className={cn(
					'flex items-center gap-1',
					readOnly && 'cursor-default self-start bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4',
					hasError && !readOnly && 'border-[2px] border-error-600 rounded-md p-1',
				)}>
				<Checkbox
					label={label}
					checked={checked !== undefined ? checked : internalChecked}
					onChange={handleChange}
					isRequired={required && !readOnly}
					labelStyle={{
						color: labelColor,
						fontWeight: labelWeight === 'semibold' ? 600 : 400,
						fontSize: getLabelFontSize(labelSize),
					}}
					disabled={readOnly}
					checkboxStyle={
						checkboxColor
							? {
									backgroundColor: checkboxColor,
									borderColor: checkboxColor,
									hoverBackgroundColor: checkboxColor,
									hoverBorderColor: checkboxColor,
								}
							: undefined
					}
				/>
				{showTooltip && (
					<Tooltip>
						<TooltipTrigger asChild>
							<button type='button' aria-label='Help information'>
								<InfoIcon size={14} className='text-neutral-400 cursor-help hover:text-neutral-600' />
							</button>
						</TooltipTrigger>
						<TooltipContent side='top' className='max-w-xs bg-white border shadow-lg px-3 py-2'>
							<MarkdownPreview markdown={tooltipText as string} className='text-xs' />
						</TooltipContent>
					</Tooltip>
				)}
			</div>
		</div>
	)
}
